function carousel(visibleCount = 1, infinite = false, intervalMs = 5000, autoplay = false) {
  function teleportScroll(el, left) {
    el.classList.remove('scroll-smooth');
    el.scrollTo({ left, behavior: 'auto' });
    setTimeout(() => el.classList.add('scroll-smooth'), 10);
  }

  return {
    // estado
    isScrollable: false,
    atStart: true,
    atEnd: false,
    isScrolling: false,
    infiniteActive: false,

    // drag
    dragStartX: null,
    dragging: false,
    dragThreshold: 10,
    swiped: false,

    // autoplay
    _timer: null,
    _scrollDebounce: null,
    _scrollEndTimer: null,

    init() {
      const el = this.$refs.carousel;
      if (!el) return;

      // Si ya está en proceso de ser inicializado por esta instancia, devolver.
      if (el.__carouselInitializing) return;

      // Helper que contiene la verdadera inicialización (clonar, listeners, etc.)
      const doSetup = () => {
        // Evitar doble inicialización completa
        if (el.__carouselInitialized) {
          // Si ya se inicializó, solo recalculamos bordes y autoplay
          this.$nextTick(() => {
            this.infiniteActive = infinite && el.hasAttribute('data-infinite-ready');
            this.isScrollable = el.scrollWidth > el.clientWidth + 1;
            this.updateEdges();
            if (autoplay && this.isScrollable) this.startAutoplay();
          });
          return;
        }

        el.__carouselInitializing = true;

        // Ocultar temporalmente para evitar flashes
        const prevVisibility = el.style.visibility;
        el.style.visibility = 'hidden';

        let infiniteSetup = infinite && el.hasAttribute('data-infinite-ready');

        // Clonado si es infinito (solo una vez) y realmente hay overflow
        if (infinite && !el.hasAttribute('data-infinite-ready')) {
          const items = Array.from(el.children);
          const totalWidth = items.reduce((sum, item) => sum + item.offsetWidth, 0);
          const scrollable = totalWidth > el.clientWidth + 1;

          if (scrollable && items.length) {
            el.setAttribute('data-infinite-ready', 'true');
            infiniteSetup = true;
            const total = items.length;

            for (let i = total - visibleCount; i < total; i++) {
              const clone = items[i].cloneNode(true);
              clone.classList.add('clone');
              el.insertBefore(clone, el.firstChild);
            }
            for (let i = 0; i < visibleCount; i++) {
              const clone = items[i].cloneNode(true);
              clone.classList.add('clone');
              el.appendChild(clone);
            }
          }
        }

        this.infiniteActive = infiniteSetup;

        this.$nextTick(() => {
          const items = el.children;

          if (this.infiniteActive) {
            const firstRealItem = items[visibleCount];
            if (firstRealItem) {
              el.classList.remove('scroll-smooth');
              el.scrollLeft = firstRealItem.offsetLeft;
              setTimeout(() => {
                el.style.visibility = prevVisibility || '';
                setTimeout(() => el.classList.add('scroll-smooth'), 50);
              }, 50);
            } else {
              el.style.visibility = prevVisibility || '';
            }
          } else {
            if (items[0]) {
              el.classList.remove('scroll-smooth');
              el.scrollLeft = items[0].offsetLeft;
              setTimeout(() => {
                el.style.visibility = prevVisibility || '';
                setTimeout(() => el.classList.add('scroll-smooth'), 50);
              }, 50);
            } else {
              el.style.visibility = prevVisibility || '';
            }
          }

          this.isScrollable = el.scrollWidth > el.clientWidth + 1;
          this.updateEdges();

          // Autoplay
          if (autoplay && this.isScrollable) this.startAutoplay();

          // Scroll listener
          const onScroll = () => {
            if (this.infiniteActive) {
              const items = el.children;
              const total = items.length;
              const firstRealIdx = visibleCount;
              const lastGroupFirstIdx = total - 2 * visibleCount;

              if (el.scrollLeft <= items[0].offsetLeft + 1) {
                teleportScroll(el, items[lastGroupFirstIdx].offsetLeft);
              } else if (el.scrollLeft >= items[total - visibleCount].offsetLeft - 1) {
                teleportScroll(el, items[firstRealIdx].offsetLeft);
              }
            }

            this.updateEdges();

            clearTimeout(this._scrollEndTimer);
            this._scrollEndTimer = setTimeout(() => {
              this.isScrolling = false;
            }, 100);

            // pausa temporal autoplay
            this.pause();
            clearTimeout(this._scrollDebounce);
            this._scrollDebounce = setTimeout(() => this.resume(), Math.max(1500, intervalMs / 2));
          };

          el.addEventListener('scroll', onScroll, { passive: true });

          // Resize
          const onResize = () => {
            const current = Math.round(el.scrollLeft / (el.children[0]?.offsetWidth || 1));
            const w = el.children[0]?.offsetWidth || el.clientWidth;
            teleportScroll(el, current * w);
            this.updateEdges();
          };
          window.addEventListener('resize', onResize, { passive: true });

          // Hover pause/resume
          this.$root.addEventListener('mouseenter', () => this.pause(), { passive: true });
          this.$root.addEventListener('mouseleave', () => this.resume(), { passive: true });

          // Drag & swipe (solo desktop / pointer fino)
          const enableDesktopDrag = window.matchMedia('(pointer: fine)').matches;
          el.onmousedown = el.onmousemove = el.onmouseup = el.onmouseleave = null;
          const setDraggingClass = (add) => {
            if (!enableDesktopDrag) return;
            el.querySelectorAll('img, a').forEach((node) => {
              if (add) node.classList.add('dragging');
              else node.classList.remove('dragging');
            });
          };

          if (enableDesktopDrag) {
            el.addEventListener('mousedown', (e) => {
              this.dragging = true;
              this.dragStartX = e.pageX;
              this.swiped = false;
              el.classList.add('dragging');
              setDraggingClass(true);
              document.body.style.userSelect = "none";
            });

            el.addEventListener('mouseup', (e) => {
              if (!this.dragging) return;
              const delta = e.pageX - this.dragStartX;
              if (Math.abs(delta) > this.dragThreshold) {
                this.swiped = true;
                if (delta < 0) this.next();
                else this.prev();
              }
              this.dragging = false;
              this.dragStartX = null;
              el.classList.remove('dragging');
              setDraggingClass(false);
              document.body.style.userSelect = "";
            });

            el.addEventListener('mouseleave', () => {
              this.dragging = false;
              this.dragStartX = null;
              el.classList.remove('dragging');
              setDraggingClass(false);
              document.body.style.userSelect = "";
            });

            el.querySelectorAll('img, a').forEach((node) => {
              node.setAttribute('draggable', 'false');
            });
          }

          el.addEventListener('click', (event) => {
            if (this.swiped) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.swiped = false;
            }
          }, true);

          // marcamos como inicializado
          el.__carouselInitialized = true;
          el.__carouselInitializing = false;
        });
      }; // end doSetup

      // Si el elemento está oculto (offsetWidth==0) esperamos hasta que tenga tamaño
      if (el.offsetWidth === 0) {
        // Si ya hay un observer anterior, no duplicamos:
        if (el.__carouselInitObserver) return;

        // MutationObserver (cuando la clase/style cambian)
        const mo = new MutationObserver(() => {
          if (el.offsetWidth > 0) {
            try { mo.disconnect(); } catch(e) {}
            try { ro.disconnect(); } catch(e) {}
            el.__carouselInitObserver = null;
            doSetup();
          }
        });
        mo.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });

        // ResizeObserver (por si el cambio viene por tamaño)
        const ro = new ResizeObserver(() => {
          if (el.offsetWidth > 0) {
            try { mo.disconnect(); } catch(e) {}
            try { ro.disconnect(); } catch(e) {}
            el.__carouselInitObserver = null;
            doSetup();
          }
        });
        ro.observe(el);

        // Guardamos referencia para evitar duplicar observers
        el.__carouselInitObserver = { mo, ro };

        // Fallback: si no se dispara nada, forzamos setup pasado 700ms (evita bloqueo)
        setTimeout(() => {
          if (el.offsetWidth > 0) {
            try { mo.disconnect(); } catch(e) {}
            try { ro.disconnect(); } catch(e) {}
            el.__carouselInitObserver = null;
            doSetup();
          }
        }, 700);

        return;
      }

      // Si ya visible, hacemos setup inmediatamente
      doSetup();
    },

    prev() {
      if (this.isScrolling) return;
      const el = this.$refs.carousel;
      if (!el || !el.children.length) return;
      if (!this.infiniteActive && this.atStart) return;

      this.isScrolling = true;
      const scrollAmount = el.children[0].offsetWidth;
      el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });

      this.updateEdges();
      this.resetAutoplay();
    },

    next() {
      if (this.isScrolling) return;
      const el = this.$refs.carousel;
      if (!el || !el.children.length) return;
      if (!this.infiniteActive && this.atEnd) return;

      this.isScrolling = true;
      const scrollAmount = el.children[0].offsetWidth;
      el.scrollBy({ left: scrollAmount, behavior: 'smooth' });

      this.updateEdges();
      this.resetAutoplay();
    },

    startAutoplay() {
      this.clearAutoplay();
      this._timer = setInterval(() => {
        if (!this.isScrolling) this.next();
      }, intervalMs);
    },
    clearAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
    pause() { this.clearAutoplay(); },
    resume() {
      if (autoplay && this.isScrollable) this.startAutoplay();
    },
    resetAutoplay() { this.pause(); this.resume(); },

    updateEdges() {
      const el = this.$refs.carousel;
      if (!el) return;

      if (this.infiniteActive) {
        this.atStart = false;
        this.atEnd = false;
        return;
      }

      const first = el.children[0];
      const last = el.children[el.children.length - 1];
      if (!first || !last) {
        this.atStart = true;
        this.atEnd = true;
        return;
      }

      const near = 2;
      this.atStart = el.scrollLeft <= (first.offsetLeft + near);
      this.atEnd = el.scrollLeft >= (last.offsetLeft - near - el.clientWidth + (last.offsetWidth || 0));
    },
  };
}
